import { ConsentChannel, ContactPointType, CUSTOMER_DEFAULT_PHONE_REGION, CustomerChannel } from '@today/types'
import { type CountryCode, parsePhoneNumberFromString } from 'libphonenumber-js'
import { z } from 'zod'

/**
 * Customer rules shared by the API and the UI (TO-418 design D8) so the form's
 * enabled channels and normalised values can never drift from the server.
 */

const emailSchema = z.email()

/**
 * Normalised form of a contact value, or `null` when it is not a valid value
 * of its type: emails are trimmed + lowercased; phones become E.164, parsed
 * with the default region (ES) when written in national format.
 */
export const normalizeContact = (type: ContactPointType, value: string): string | null => {
	const trimmed = value.trim()
	if (type === ContactPointType.EMAIL) {
		const lowered = trimmed.toLowerCase()
		return emailSchema.safeParse(lowered).success ? lowered : null
	}
	const phone = parsePhoneNumberFromString(trimmed, CUSTOMER_DEFAULT_PHONE_REGION as CountryCode)
	return phone?.isValid() ? phone.number : null
}

/** Contact types that make each preferred channel reachable (empty = always reachable). */
const CHANNEL_NEEDS: Record<CustomerChannel, ContactPointType[]> = {
	[CustomerChannel.EMAIL]: [ContactPointType.EMAIL],
	[CustomerChannel.WHATSAPP]: [ContactPointType.MOBILE],
	[CustomerChannel.SMS]: [ContactPointType.MOBILE],
	[CustomerChannel.PHONE]: [ContactPointType.MOBILE, ContactPointType.LANDLINE],
	[CustomerChannel.IN_STORE]: [],
}

/** True when `channel` (or no channel) can be used with the given live contact types. */
export const isChannelReachable = (
	channel: CustomerChannel | null | undefined,
	types: readonly ContactPointType[],
): boolean => {
	// null OR undefined → "no preferred channel → always reachable". A caller that
	// reaches this before Zod normalised `preferred_channel` (undefined → null) must
	// not crash on CHANNEL_NEEDS[undefined].
	if (channel === null || channel === undefined) return true
	const needs = CHANNEL_NEEDS[channel]
	return needs.length === 0 || needs.some((type) => types.includes(type))
}

/** Every channel reachable with the given live contact types, in enum order. */
export const reachableChannels = (types: readonly ContactPointType[]): CustomerChannel[] =>
	Object.values(CustomerChannel).filter((channel) => isChannelReachable(channel, types))

/**
 * Masked form shown to callers without `customer.pii.read`: an email keeps the
 * first character of the local part and the domain; a phone keeps its first 3
 * and last 3 characters.
 */
export const maskContactValue = (type: ContactPointType, value: string): string => {
	if (type === ContactPointType.EMAIL) {
		const at = value.lastIndexOf('@')
		return `${value.charAt(0)}***@${value.slice(at + 1)}`
	}
	return `${value.slice(0, 3)}${'*'.repeat(Math.max(value.length - 6, 0))}${value.slice(-3)}`
}

/** Contact types a consent on `channel` may reference (`any` references none). */
const CONSENT_ACCEPTS: Record<ConsentChannel, ContactPointType[]> = {
	[ConsentChannel.EMAIL]: [ContactPointType.EMAIL],
	[ConsentChannel.SMS]: [ContactPointType.MOBILE],
	[ConsentChannel.WHATSAPP]: [ContactPointType.MOBILE],
	[ConsentChannel.PHONE]: [ContactPointType.MOBILE, ContactPointType.LANDLINE],
	[ConsentChannel.ANY]: [],
}

/** True when a consent on `channel` may reference a contact point of `type` (`null` = none referenced). */
export const consentChannelAccepts = (channel: ConsentChannel, type: ContactPointType | null): boolean =>
	type === null || CONSENT_ACCEPTS[channel].includes(type)

/** Normalised forms a search term could match exactly: lowercased, plus E.164 when it parses as a phone. */
export const searchContactCandidates = (term: string): string[] => {
	const lowered = term.trim().toLowerCase()
	const phone = parsePhoneNumberFromString(lowered, CUSTOMER_DEFAULT_PHONE_REGION as CountryCode)
	const e164 = phone?.isValid() ? phone.number : null
	return e164 && e164 !== lowered ? [lowered, e164] : [lowered]
}
